/**
 * ACADS — the Kolkata tuition business, from September 2026 on.
 *
 * What is left here is the month dashboard for the HQ view (every teacher row
 * opens that teacher's collection sheet) and the styles the ACADS pages share.
 * The monthly entry grid and the student list that used to live here are gone:
 * collecting, correcting a payment and editing a student all happen on the
 * collect screens (AcadsCollect).
 */
import {
  Chip,
  CircularProgress,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useEffect, useState } from 'react'

import { fetchAcadsDispatch, fetchAcadsEntry, fetchAcadsSummary } from 'api'
import constants from 'app/constants'
import { showToast } from 'app/helpers'

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1.5)
  },
  grow: { flexGrow: 1 },
  muted: { color: theme.palette.text.secondary },

  scroll: { overflow: 'auto', maxHeight: 'calc(100vh - 260px)' },
  table: {
    borderCollapse: 'separate',
    borderSpacing: 0,
    width: 'max-content',
    minWidth: '100%'
  },
  cell: {
    whiteSpace: 'nowrap',
    padding: '3px 6px',
    borderBottom: '1px solid #ececec'
  },
  head: {
    position: 'sticky',
    top: 0,
    zIndex: 3,
    height: 30,
    background: '#f4f6f8',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    color: '#555'
  },
  filterHead: {
    position: 'sticky',
    top: 30,
    zIndex: 3,
    height: 34,
    background: '#f4f6f8',
    padding: '2px 4px',
    borderBottom: '2px solid #cfd6de'
  },
  froze: { position: 'sticky', zIndex: 2, background: '#fbfbfc' },
  frozeHead: { background: '#f4f6f8' },
  // The frozen heading cells stick both ways, so they must sit above the
  // frozen body cells (2) and the scrolling heading cells (3) — otherwise the
  // rows slide over the heading of the frozen columns.
  corner: { zIndex: 4 },
  edge: { borderRight: '2px solid #cfd6de' },
  dirty: { background: '#fffbe6' },
  num: { textAlign: 'right' },
  cellInput: {
    '& input': { padding: '3px 4px', fontSize: 13 },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '&:hover .MuiOutlinedInput-notchedOutline': { border: '1px solid #ccd' },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: '1px solid #1a5fd0'
    }
  },
  filterInput: { '& input': { padding: '4px 6px', fontSize: 12 } },

  // Four tiles, not ten. Each answers one question and carries its own
  // supporting line, so the detail is next to the number it explains instead
  // of being another tile competing with it.
  tiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  tile: {
    border: '1px solid #e4e7ec',
    borderRadius: 10,
    padding: theme.spacing(2, 2.25)
  },
  tileKey: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: '#666'
  },
  tileVal: {
    fontSize: 30,
    fontWeight: 600,
    marginTop: 4,
    lineHeight: 1.1,
    fontVariantNumeric: 'tabular-nums'
  },
  tileNote: {
    fontSize: 12.5,
    color: '#5a6169',
    marginTop: 6,
    lineHeight: 1.45
  },
  warn: { color: '#a4501a' },
  good: { color: '#14532d' },
  sectionTitle: {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#444',
    margin: theme.spacing(2.5, 0, 1)
  },
  subTabs: {
    borderBottom: '1px solid #e4e7ec',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    minHeight: 38
  },
  clickRow: { cursor: 'pointer', '&:hover': { background: '#f0f6ff' } },
  picked: { background: '#fff6dc' },
  subtle: { fontSize: 12, color: theme.palette.text.secondary },
  drill: {
    cursor: 'pointer',
    borderBottom: '1px dotted currentColor',
    '&:hover': { color: theme.palette.primary.main }
  },
  addBar: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
    padding: theme.spacing(1.25),
    marginBottom: theme.spacing(1.5),
    background: '#f5f8fc',
    border: '1px solid #dbe5f0',
    borderRadius: 6
  },
  empty: { padding: theme.spacing(4), color: theme.palette.text.secondary }
}))

const inr = (v) =>
  v === null || v === undefined
    ? '—'
    : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`

// ------------------------------------------------------------------ dashboard

const Tile = ({ label, value, note, tone, classes }) => (
  <div className={classes.tile}>
    <div className={classes.tileKey}>{label}</div>
    <div className={[classes.tileVal, tone ? classes[tone] : ''].join(' ')}>
      {value}
    </div>
    {note ? <div className={classes.tileNote}>{note}</div> : null}
  </div>
)

// HQ's view of the dispatch sheets: what each teacher's students paid in the
// month (by date of receipt), the split, and how the payout went.
const PayoutTable = ({ month, classes }) => {
  const [data, setData] = useState(null)
  const [showModes, setShowModes] = useState(false)
  useEffect(() => {
    let live = true
    setData(null)
    fetchAcadsDispatch(month)
      .then((res) => live && setData(res))
      .catch(
        () => live && showToast('Could not load the payout', constants.ERROR)
      )
    return () => {
      live = false
    }
  }, [month])
  if (!data) return <CircularProgress size={22} />
  const modes = showModes ? data.modes : []
  const money = (v) => (v === null || v === undefined ? '—' : inr(v))
  const cols = [
    ['revenue', 'Revenue'],
    ['teacher_share', "Teacher's share"],
    ['paid_to_teacher', 'Paid to teacher'],
    ['net_payable', 'Net payable'],
    ['bank', 'Bank'],
    ['cash', 'Cash'],
    ['oh_share', 'OH share'],
    ['gst', 'GST on OH share']
  ]
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          margin: '8px 0'
        }}
      >
        <Typography variant="body2" className={classes.muted}>
          Fees received in {data.month_label}, by date of receipt. Bank and cash
          are what was saved on the Dispatch sheet.
        </Typography>
        <Chip
          size="small"
          label="Show payment modes"
          clickable
          color={showModes ? 'primary' : 'default'}
          onClick={() => setShowModes(!showModes)}
        />
      </div>
      <div style={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Teacher</TableCell>
              {modes.map((m) => (
                <TableCell key={m} align="right">
                  {m}
                </TableCell>
              ))}
              {cols.map(([key, label]) => (
                <TableCell key={key} align="right">
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.teachers.map((t) => (
              <TableRow key={t.teacher_uuid}>
                <TableCell>{t.teacher}</TableCell>
                {modes.map((m) => (
                  <TableCell key={m} align="right">
                    {t.modes[m] ? inr(t.modes[m]) : '—'}
                  </TableCell>
                ))}
                {cols.map(([key]) => (
                  <TableCell
                    key={key}
                    align="right"
                    style={
                      key === 'net_payable' ? { fontWeight: 600 } : undefined
                    }
                    title={
                      (key === 'bank' || key === 'cash') && t.bank === null
                        ? 'Not saved yet: set it on the Dispatch sheet'
                        : undefined
                    }
                  >
                    {money(t[key])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {!data.teachers.length && (
              <TableRow>
                <TableCell colSpan={cols.length + 1} className={classes.empty}>
                  No fees received in {data.month_label}.
                </TableCell>
              </TableRow>
            )}
            {data.teachers.length > 0 && (
              <TableRow>
                <TableCell>
                  <b>Total</b>
                </TableCell>
                {modes.map((m) => (
                  <TableCell key={m} align="right">
                    <b>
                      {inr(
                        data.teachers.reduce(
                          (sum, t) => sum + (t.modes[m] || 0),
                          0
                        )
                      )}
                    </b>
                  </TableCell>
                ))}
                {cols.map(([key]) => (
                  <TableCell key={key} align="right">
                    <b>{inr(data.total[key])}</b>
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

const DashboardTab = ({ month, onOpen, classes }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  // Which breakdown is open at the bottom. Teacher wise first — it is the
  // worklist, and it is what somebody opening this page is here to act on.
  const [view, setView] = useState('teacher')
  // A figure in the teacher table opens the student-by-student list it is the
  // total of, for this month.
  const drill = (teacher, kind, label) =>
    onOpen ? (
      <span
        role="button"
        tabIndex={0}
        className={classes.drill}
        title="See the students this figure is made of"
        onClick={() => onOpen(kind, teacher)}
        onKeyDown={(e) => e.key === 'Enter' && onOpen(kind, teacher)}
      >
        {label}
      </span>
    ) : (
      label
    )

  useEffect(() => {
    let live = true
    setLoading(true)
    fetchAcadsSummary(month)
      .then((res) => live && setData(res))
      .catch(
        () => live && showToast('Could not load the dashboard', constants.ERROR)
      )
      .finally(() => live && setLoading(false))
    return () => {
      live = false
    }
  }, [month])

  if (loading && !data) return <CircularProgress size={22} />
  if (!data) return null

  return (
    <div>
      {/* Four tiles, each answering one question and carrying its own detail
          line. Ten small tiles read as a wall of numbers; the eye picks none of
          them. */}
      <div className={classes.tiles}>
        <Tile
          label="Left to enter"
          value={`${data.remaining}`}
          tone={data.remaining ? 'warn' : 'good'}
          note={`of ${data.students} students · ${data.teachers_done} of ${data.teachers_total} teachers done`}
          classes={classes}
        />
        <Tile
          label="Collected"
          value={inr(data.collected)}
          tone="good"
          note={`of ${inr(data.expected)} expected · ${
            data.paid
          } students paid`}
          classes={classes}
        />
        <Tile
          label="Outstanding"
          value={inr(data.pending)}
          tone={data.pending > 0 ? 'warn' : 'good'}
          note={
            data.arrears
              ? `this month · ${inr(data.arrears)} more carried from earlier`
              : 'this month · nothing carried from earlier'
          }
          classes={classes}
        />
        <Tile
          label="OH share"
          value={inr(data.split.oh)}
          note={`teacher ${inr(data.split.teacher)} · GST ${inr(
            data.split.gst
          )}`}
          classes={classes}
        />
      </div>
      {data.signoff && data.signoff.approved && (
        <Typography
          variant="body2"
          className={classes.muted}
          style={{ marginBottom: 12 }}
        >
          {`Signed off by ${
            data.signoff.approved_by || 'HQ Finance'
          } — this month is closed for edits.`}
        </Typography>
      )}

      {/* The breakdowns live under tabs rather than stacked, so the page ends
          with one table instead of three competing for the same attention.
          "By teacher" is first and is the worklist: ordered by who has the most
          left, finished teachers last, so the order is itself the instruction. */}
      <Tabs
        value={view}
        onChange={(e, v) => setView(v)}
        indicatorColor="primary"
        textColor="primary"
        className={classes.subTabs}
      >
        <Tab value="teacher" label="Teacher wise" />
        <Tab value="mode" label="By payment mode" />
        <Tab value="centre" label="By centre" />
        <Tab value="payout" label="Teacher payout" />
      </Tabs>

      {view === 'payout' && <PayoutTable month={month} classes={classes} />}

      {view === 'teacher' && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Teacher</TableCell>
              <TableCell>Students</TableCell>
              <TableCell>Left to enter</TableCell>
              <TableCell>Not paid</TableCell>
              <TableCell align="right">Arrears</TableCell>
              <TableCell align="right">Expected</TableCell>
              <TableCell align="right">Collected</TableCell>
              <TableCell align="right">Pending</TableCell>
              <TableCell align="right">OH share</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.teachers.map((t) => (
              <TableRow key={t.teacher_uuid}>
                <TableCell>{t.teacher}</TableCell>
                <TableCell>{drill(t, 'students', t.students)}</TableCell>
                <TableCell>
                  {t.done ? (
                    <Chip size="small" label="done" />
                  ) : (
                    <Chip
                      size="small"
                      color="primary"
                      label={`${t.remaining} left`}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {t.unpaid ? (
                    <Chip
                      size="small"
                      color="secondary"
                      label={t.unpaid}
                      clickable={Boolean(onOpen)}
                      onClick={onOpen ? () => onOpen('unpaid', t) : undefined}
                    />
                  ) : (
                    '0'
                  )}
                </TableCell>
                <TableCell align="right">
                  {t.arrears ? drill(t, 'arrears', inr(t.arrears)) : '—'}
                </TableCell>
                <TableCell align="right">
                  {drill(t, 'expected', inr(t.expected))}
                </TableCell>
                <TableCell align="right">
                  {drill(t, 'collected', inr(t.collected))}
                </TableCell>
                <TableCell align="right">
                  {drill(t, 'pending', inr(t.pending))}
                </TableCell>
                <TableCell align="right">{inr(t.split.oh)}</TableCell>
              </TableRow>
            ))}
            {!data.teachers.length && (
              <TableRow>
                <TableCell colSpan={9} className={classes.empty}>
                  No students yet. Add them from Collect a fee.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {view === 'mode' && (
        <Table size="small">
          <TableBody>
            {Object.keys(data.by_mode)
              .filter((mode) => data.by_mode[mode])
              .map((mode) => (
                <TableRow key={mode}>
                  <TableCell>{mode}</TableCell>
                  <TableCell align="right">{inr(data.by_mode[mode])}</TableCell>
                </TableRow>
              ))}
            {!Object.keys(data.by_mode).some((m) => data.by_mode[m]) && (
              <TableRow>
                <TableCell className={classes.empty}>
                  Nothing collected yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {view === 'centre' && (
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell>Theatre Road</TableCell>
              <TableCell align="right">{inr(data.by_centre.TR)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Salt Lake</TableCell>
              <TableCell align="right">{inr(data.by_centre.SL)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}
    </div>
  )
}

// ------------------------------------------------------------ figure breakup

// What each dashboard figure is the total of. `only` picks the students that
// make the figure up; `column` is the one whose total equals it.
const FIGURES = {
  students: { label: 'Students', column: '', only: () => true },
  expected: { label: 'Expected', column: 'expected', only: () => true },
  collected: {
    label: 'Collected',
    column: 'collected',
    only: (r) => r.collected > 0
  },
  pending: {
    label: 'Pending',
    column: 'pending',
    only: (r) => r.pending !== 0
  },
  unpaid: { label: 'Not paid', column: '', only: (r) => !r.collected },
  arrears: { label: 'Arrears', column: 'arrears', only: (r) => r.arrears > 0 }
}

/**
 * One teacher's month, student by student — the list a dashboard figure is
 * the total of. It reads the same rows the dashboard adds up, so the total at
 * the bottom of the highlighted column is the number that was clicked.
 */
const TeacherBreakup = ({ month, teacher, figure, classes }) => {
  const [rows, setRows] = useState(null)
  const [label, setLabel] = useState('')
  const [all, setAll] = useState(false)

  useEffect(() => {
    setRows(null)
    fetchAcadsEntry(month, teacher.teacher_uuid)
      .then((data) => {
        setLabel(data.month_label)
        setRows(
          data.rows.map((r) => {
            const expected = Number(r.fees_due || 0)
            const collected = Number(r.amount_paid || 0)
            return {
              key: r.enrolment,
              student: r.student_name,
              about: [
                r.class_label && `Class ${r.class_label}`,
                r.centre,
                r.parent_contact
              ]
                .filter(Boolean)
                .join(' · '),
              expected,
              collected,
              pending: expected - collected,
              arrears: Number(r.arrears || 0),
              arrearsMonths: r.arrears_months,
              paid: [
                r.mode,
                r.date_paid &&
                  new Date(r.date_paid).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'short'
                  })
              ]
                .filter(Boolean)
                .join(' · ')
            }
          })
        )
      })
      .catch(() => {
        setRows([])
        showToast('Could not load the students', constants.ERROR)
      })
  }, [month, teacher.teacher_uuid])

  if (!rows) return <CircularProgress size={24} />

  const what = FIGURES[figure] || FIGURES.expected
  const shown = all ? rows : rows.filter(what.only)
  const total = (key) => shown.reduce((sum, r) => sum + r[key], 0)
  const mark = (key) => (what.column === key ? classes.picked : undefined)
  const money = (key) => (
    <TableCell align="right" className={mark(key)}>
      <b>{inr(total(key))}</b>
    </TableCell>
  )

  return (
    <div>
      <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
        <b>{teacher.teacher}</b> · {label} · <b>{what.label}</b>
        {what.column ? ` ${inr(total(what.column))}` : ''} · {shown.length}{' '}
        student{shown.length === 1 ? '' : 's'}
        {shown.length !== rows.length || all ? (
          <Chip
            size="small"
            clickable
            style={{ marginLeft: 12 }}
            color={all ? 'primary' : 'default'}
            label={
              all
                ? `Showing all ${rows.length} students`
                : `Show all ${rows.length} students`
            }
            onClick={() => setAll(!all)}
          />
        ) : null}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Student</TableCell>
            <TableCell align="right" className={mark('expected')}>
              Expected
            </TableCell>
            <TableCell align="right" className={mark('collected')}>
              Collected
            </TableCell>
            <TableCell align="right" className={mark('pending')}>
              Pending
            </TableCell>
            <TableCell align="right" className={mark('arrears')}>
              Arrears (earlier months)
            </TableCell>
            <TableCell>Paid by</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shown.map((r) => (
            <TableRow key={r.key}>
              <TableCell>
                <b>{r.student}</b>
                <div className={classes.subtle}>{r.about}</div>
              </TableCell>
              <TableCell align="right" className={mark('expected')}>
                {inr(r.expected)}
              </TableCell>
              <TableCell align="right" className={mark('collected')}>
                {r.collected ? inr(r.collected) : '—'}
              </TableCell>
              <TableCell align="right" className={mark('pending')}>
                {r.pending ? inr(r.pending) : '—'}
              </TableCell>
              <TableCell
                align="right"
                className={mark('arrears')}
                title={r.arrearsMonths}
              >
                {r.arrears ? inr(r.arrears) : '—'}
              </TableCell>
              <TableCell>{r.paid || (r.collected ? '' : 'Not paid')}</TableCell>
            </TableRow>
          ))}
          {!shown.length && (
            <TableRow>
              <TableCell colSpan={6} className={classes.empty}>
                No students in this figure.
              </TableCell>
            </TableRow>
          )}
          <TableRow>
            <TableCell>
              <b>Total · {shown.length} students</b>
            </TableCell>
            {money('expected')}
            {money('collected')}
            {money('pending')}
            {money('arrears')}
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>
    </div>
  )
}

export { DashboardTab, TeacherBreakup, useStyles }
